// One-shot client rescue — lift device-held orphaned counts off this device.
//
// PHASE 1 (RESCUE) of the offline-capture-loss recovery. When an offline count
// can never sync (its parent session/building/space was removed or re-seeded
// server-side), the connector preserves it in the local-only
// `orphaned_capture_items` table (connector-quarantine.ts) — but there is no
// in-app re-inject path, and the device may be one we cannot physically reach.
// This reads every held count, enriches it with the device's still-local space
// and session context (so PHASE 2 placement can map it to a current room), and
// POSTs the batch to the server inbox `POST /capture/recovery-dump`.
//
// SAFETY: this only READS local tables (it does NOT delete the held rows — the
// on-device backup stays intact) and writes to an isolated server inbox. It
// never mutates live `capture_items`, the dashboard, the CSV export, or sync.
// Re-running is safe: the server upserts on (user, original_item_id).
//
// DI seam: this module is pure of the PowerSync singleton and the network so
// tests can drive it with a fake db + a fake `post`, and so importing it never
// constructs a real PowerSyncDatabase (the connector modules follow the same
// rule). The real wiring (singleton + apiFetch) lives in three callers:
// `auto-rescue-held-captures.tsx`, the headless component (mounted in main.tsx)
// that fires this automatically on an authenticated app load (no route, no UI);
// `logout-cleanup.ts` :: preserveHeldCapturesBeforeClear() — the H8/H9
// rescue-before-clear fired on sign-out (auth-provider.tsx); and the unlinked
// pilot page `recover-held-captures-page.tsx`.

import type { CaptureRecoveryDumpInsert } from '@citycare/shared'

import {
  hasPreservablePayload,
  toQueuedDumpItemFromCrud,
} from './queued-crud-synthesis'

/** Minimal slice of the PowerSync db this module needs (read-only). */
export interface ReadableDb {
  getAll: (
    sql: string,
    params?: unknown[],
  ) => Promise<Record<string, unknown>[]>
}

// One held count shipped to the server inbox — derived from the shared table
// type so there is one source of truth (a new column propagates automatically
// instead of silently drifting). The server fills id / user / recovered_at, so
// they're omitted. `raw` is the verbatim local row (forensic backstop). (#10)
export type RecoveryDumpItem = Omit<
  CaptureRecoveryDumpInsert,
  'id' | 'userId' | 'userDisplayName' | 'recoveredAt'
>

export interface RecoveryDumpResponse {
  received: number
  stored: number
}

export type DumpPoster = (
  items: RecoveryDumpItem[],
) => Promise<RecoveryDumpResponse>

export interface RescueResult {
  /** Held counts found on this device. */
  found: number
  /** Rows the server confirmed it stored. */
  sent: number
}

/**
 * The device's un-uploaded held work split by how the offline-logout re-inject
 * must handle each dimension (#809 Tier-3 — see held-work-offline-stash.ts):
 *   - `queueItems`      — ps_crud-QUEUED capture_items: un-uploaded but perfectly
 *     SYNCABLE counts. On re-login they are re-INSERTed into the live capture
 *     path so they re-enter ps_crud and upload normally — the surveyor sees the
 *     queue drain on the device, and the counts land as live `capture_items`.
 *   - `quarantineItems` — `orphaned_capture_items`: counts the connector already
 *     FAILED to upload (404 / invalid_argument). They cannot sync as-is, so they
 *     ship to the isolated server recovery inbox for admin Phase-2 placement.
 * Keeping the two distinct is the whole point of the re-queue fix: only the
 * structurally-broken `quarantineItems` belong in the inbox; the `queueItems`
 * must sync like any other offline capture (#809 follow-up).
 */
export interface HeldWorkBundle {
  queueItems: RecoveryDumpItem[]
  quarantineItems: RecoveryDumpItem[]
  /** True when the pending ps_crud queue exceeded the read ceiling and an unread
   *  overflow is at risk of being wiped by the logout clear (Codex :316). Carried on
   *  the bundle for the stash audit to surface loudly; NOT part of the stored stash. */
  truncated?: boolean
}

// Read every held count and LEFT JOIN the device's still-local space + session
// context. LEFT JOIN (not INNER) so a held count whose space/session is already
// gone locally still comes through — with null context — rather than vanishing.
const HELD_CAPTURES_SQL = `
  SELECT
    o.id                 AS original_item_id,
    o.capture_session_id AS capture_session_id,
    o.site_id            AS site_id,
    o.building_id        AS building_id,
    o.space_id           AS space_id,
    o.asset_type_id      AS asset_type_id,
    o.quantity           AS quantity,
    o.is_zero_confirmed  AS is_zero_confirmed,
    o.notes              AS notes,
    o.captured_at        AS captured_at,
    o.orphaned_at        AS orphaned_at,
    o.reason             AS reason,
    s.display_name       AS space_display_name,
    s.room_type_id       AS room_type_id,
    s.floor_id           AS floor_id,
    s.serial_number      AS serial_number,
    cs.building_id       AS session_building_id,
    cs.site_id           AS session_site_id,
    cs.started_at        AS session_started_at,
    cs.source_device_id  AS source_device_id
  FROM orphaned_capture_items o
  LEFT JOIN spaces s ON s.id = o.space_id
  LEFT JOIN capture_sessions cs ON cs.id = o.capture_session_id
`

function str(value: unknown): string | null {
  return value === null || value === undefined ? null : String(value)
}

function num(value: unknown): number | null {
  return value === null || value === undefined ? null : Number(value)
}

export function toDumpItem(row: Record<string, unknown>): RecoveryDumpItem {
  return {
    originalItemId: String(row['original_item_id']),
    captureSessionId: str(row['capture_session_id']),
    siteId: str(row['site_id']),
    buildingId: str(row['building_id']),
    spaceId: str(row['space_id']),
    assetTypeId: str(row['asset_type_id']),
    quantity: str(row['quantity']),
    isZeroConfirmed: num(row['is_zero_confirmed']),
    notes: str(row['notes']),
    capturedAt: str(row['captured_at']),
    orphanedAt: str(row['orphaned_at']),
    reason: str(row['reason']),
    sourceDeviceId: str(row['source_device_id']),
    spaceDisplayName: str(row['space_display_name']),
    roomTypeId: str(row['room_type_id']),
    floorId: str(row['floor_id']),
    serialNumber: num(row['serial_number']),
    sessionBuildingId: str(row['session_building_id']),
    sessionSiteId: str(row['session_site_id']),
    sessionStartedAt: str(row['session_started_at']),
    raw: row,
  }
}

// Ship in chunks so one POST never exceeds the server's per-call item cap and
// stays well under Postgres's bind-parameter limit. (audit-1 #4) Exported so the
// offline-logout re-inject (held-work-offline-stash.ts) chunks identically.
export const POST_CHUNK = 1000

/**
 * Read held counts, ship them to the server inbox in bounded chunks. Pure of the
 * singleton/network for testability. Returns counts found vs. stored. Throws
 * only if a POST fails (the caller surfaces it); an empty table is a no-op
 * success.
 */
export async function rescueHeldCaptures(
  db: ReadableDb,
  post: DumpPoster,
): Promise<RescueResult> {
  const items = await readQuarantinedItems(db)
  return postDumpItems(items, post, items.length)
}

/** READ-ONLY: the device-held quarantine (`orphaned_capture_items`) as dump
 *  items. Shared by the online quarantine rescue and the offline-logout stash
 *  (held-work-offline-stash.ts) so both ship the identical shape. */
export async function readQuarantinedItems(
  db: ReadableDb,
): Promise<RecoveryDumpItem[]> {
  return (await db.getAll(HELD_CAPTURES_SQL)).map(toDumpItem)
}

// ─── F3 (#809): ps_crud upload-queue rescue ─────────────────────────────────
//
// The quarantine rescue above covers counts the connector PRESERVED because they
// can never sync (orphaned_capture_items). It does NOT cover the OTHER device-
// held dimension: capture_items writes still sitting in the PowerSync `ps_crud`
// upload queue — un-uploaded but perfectly syncable counts. On sign-out
// `disconnectAndClear` wipes that queue, and the H8/H9 rescue shipped only the
// quarantine, so a surveyor who confirms sign-out with a queued backlog (e.g. a
// device that was offline, or one whose queue jammed) PERMANENTLY loses those
// counts (Sentry `1P`: 7,119 ops discarded on one logout — #809 / "H10").
//
// This is the Tier-2 fix: ship the queued capture_items counts to the SAME
// isolated server inbox (`POST /capture/recovery-dump`) BEFORE the clear, exactly
// like the quarantine rescue. It is READ-ONLY on the device — it reads the
// pending CRUD batch (NEVER calls `batch.complete()`, which would drain ps_crud
// and defeat a still-online upload) and the live capture_items rows, and writes
// ONLY to the inbox. Idempotent: the inbox upserts on (user, original_item_id),
// the capture_items id, so a row that ALSO uploads normally is harmless (the
// inbox row is never auto-placed; Phase-2 placement is admin-gated + idempotent
// on the capture_items PK). Pure of the singleton/network for testability.

/** The pending-CRUD-queue + local-row reads this rescue needs (read-only). */
export interface UploadQueueReadableDb extends ReadableDb {
  /**
   * Read the pending CRUD upload queue WITHOUT acking it. Mirrors
   * `PowerSyncDatabase.getCrudBatch(limit?)` — returns up to `limit` queued ops
   * (or null when empty); `haveMore` flags that the queue exceeds `limit`. We
   * never call the returned batch's `complete()`, so this does not drain ps_crud.
   */
  getCrudBatch: (limit?: number) => Promise<{
    crud: ReadonlyArray<{
      op: string
      table: string
      id: string
      // The queued op's payload + trackMetadata snapshot. Carried so a queued
      // count whose LIVE capture_items row was already sync-deleted (the bare-row
      // case) can still be rescued from the queue entry itself — opData holds the
      // count (quantity/is_zero_confirmed/notes), metadata the FK context the
      // increment snapshotted (schema.ts trackMetadata). Optional: a reader that
      // does not surface them yields the live-row-only behaviour. (Codex P1)
      opData?: Record<string, unknown> | null
      metadata?: string
    }>
    haveMore: boolean
  } | null>
}

// Read the WHOLE pending queue in one bounded pass. A real device queue is at
// most a few thousand entries (the 7,119 incident is the worst case seen); this
// ceiling reads that comfortably in one call while capping a pathological queue.
// We cannot paginate without `complete()` (which would drain ps_crud), so a
// queue beyond this is reported (NOT silently truncated) and the remainder rides
// the on-device backup until the next rescue. (no-silent-cap rule)
const QUEUE_READ_LIMIT = 100_000

// All local capture_items + their still-local space/session context, mirroring
// HELD_CAPTURES_SQL but sourced from the LIVE table (the rows the queue points at).
// `rejected_at` is PROJECTED (not filtered in SQL) so a SINGLE read serves THREE needs:
// the live-row re-queue (non-rejected rows), the admin-rejected-tombstone routing (a
// queued count that synced back rejected must NOT be silently dropped by the logout
// clear — it is routed to the recovery inbox instead, Codex :378), AND the truly-gone
// detection (an id absent from this WHOLE set is sync-deleted, not merely rejected, so
// its count is rescued from the queued op's opData+metadata — Codex P1). Filtered to
// the queued ids in JS
// (one read, no per-id round-trips, no IN-list the local engine must expand).
const QUEUED_CAPTURES_SQL = `
  SELECT
    ci.id                AS original_item_id,
    ci.capture_session_id AS capture_session_id,
    ci.site_id           AS site_id,
    ci.building_id       AS building_id,
    ci.space_id          AS space_id,
    ci.asset_type_id     AS asset_type_id,
    ci.quantity          AS quantity,
    ci.is_zero_confirmed AS is_zero_confirmed,
    ci.notes             AS notes,
    ci.captured_at       AS captured_at,
    ci.rejected_at       AS rejected_at,
    s.display_name       AS space_display_name,
    s.room_type_id       AS room_type_id,
    s.floor_id           AS floor_id,
    s.serial_number      AS serial_number,
    cs.building_id       AS session_building_id,
    cs.site_id           AS session_site_id,
    cs.started_at        AS session_started_at,
    cs.source_device_id  AS source_device_id
  FROM capture_items ci
  LEFT JOIN spaces s ON s.id = ci.space_id
  LEFT JOIN capture_sessions cs ON cs.id = ci.capture_session_id
`

// A queued count is sourced from the LIVE row, not the quarantine, so `reason`
// records WHERE the rescue found it (Phase-2 placement / forensics) and
// `orphanedAt` is null (it was never orphaned — just un-uploaded).
function toQueuedDumpItem(row: Record<string, unknown>): RecoveryDumpItem {
  return { ...toDumpItem(row), reason: 'logout_queue_rescue', orphanedAt: null }
}

// Exported so the offline-stash re-inject (logout-cleanup.ts) ships the stashed
// items in the SAME bounded chunks as the online rescues.
export function postDumpItems(
  items: RecoveryDumpItem[],
  post: DumpPoster,
  found: number,
): Promise<RescueResult> {
  return (async () => {
    let sent = 0
    for (let i = 0; i < items.length; i += POST_CHUNK) {
      const res = await post(items.slice(i, i + POST_CHUNK))
      sent += res.stored
    }
    return { found, sent }
  })()
}

/**
 * Read the pending ps_crud queue, ship the queued capture_items counts to the
 * server inbox in bounded chunks. Pure of the singleton/network. Returns counts
 * found vs. stored. Throws only if a POST fails (the caller surfaces it); an
 * empty queue / no queued capture_items is a no-op success. Never drains ps_crud.
 */
export async function rescueUploadQueue(
  db: UploadQueueReadableDb,
  post: DumpPoster,
): Promise<RescueResult> {
  const items = await readQueuedItems(db)
  return postDumpItems(items, post, items.length)
}

/**
 * READ-ONLY: the ps_crud-queued capture_items counts as dump items, SPLIT by
 * LIVE-vs-rejected so the offline-logout re-inject routes each safely (the online
 * queue rescue, which ships everything to the inbox, ignores the split via
 * `readQueuedItems`):
 *   - `createItems`     — every LIVE queued row (`rejected_at IS NULL`), whether the
 *     queue holds a PUT (fresh offline create) or ONLY PATCH(es) (an edit to a row that
 *     was already synced before this offline session). BOTH re-enter the live capture
 *     path (`requeueStashedCounts`): a create re-INSERTs and uploads as a normal create;
 *     an edit collides with its synced-down row and RE-APPLIES as a sync_version-bumped
 *     PATCH (requeue-stashed-counts.ts :155). Earlier, PATCH-only edits were routed to
 *     the inbox to avoid an idempotent-create dropping a decrement-to-0 — but the inbox
 *     CANNOT apply an edit (Phase-2 placement's `onConflictDoNothing(id)` no-ops the
 *     existing PK), silently dropping it (Codex P1). A real PATCH re-apply is correct, so
 *     live edits now ride this dimension. The re-inject is gated on PowerSync first-sync
 *     (auto-reinject-stashed-work.tsx) so the edited row is present for the collision.
 *   - `updateOnlyItems` — the inbox dimension: a queued id whose row synced back down as
 *     an admin-rejected TOMBSTONE (`rejected_at` set). Re-creating it would resurrect a
 *     count the admin rejected, so it ships to the recovery inbox (preserved +
 *     admin-surfaced) instead — the one case that does NOT re-enter the live path.
 * A create-then-increment offline session has BOTH a PUT and PATCHes for its id, so it
 * is (correctly) a create — `updateOnlyIds` excludes any id that also has a PUT, but
 * the live/rejected split below is what actually routes it.
 * Reads the pending CRUD batch (NEVER `complete()`s it — that would drain ps_crud)
 * and the live rows.
 */
export async function readQueuedItemsSplit(db: UploadQueueReadableDb): Promise<{
  createItems: RecoveryDumpItem[]
  updateOnlyItems: RecoveryDumpItem[]
  // True when the pending queue exceeded QUEUE_READ_LIMIT (haveMore): the overflow is
  // NOT captured and the logout clear would wipe it — the caller (offline-logout stash)
  // surfaces this LOUDLY so the loss is never silent (Codex :316).
  truncated: boolean
}> {
  const empty = (truncated: boolean) => ({
    createItems: [],
    updateOnlyItems: [],
    truncated,
  })
  const batch = await db.getCrudBatch(QUEUE_READ_LIMIT)
  if (batch === null || batch.crud.length === 0) return empty(false)
  if (batch.haveMore) {
    // Loud, never silent: a queue beyond the read ceiling is only partially captured.
    // Online (Tier-2 rescue) the remainder rides ps_crud's on-device backup; at OFFLINE
    // logout the subsequent clear WIPES that backup, so the caller audits the truncation
    // (the `truncated` flag) rather than relying on this console warning alone (:316).
    console.warn(
      `[rescue-upload-queue] pending queue exceeds ${QUEUE_READ_LIMIT} entries — ` +
        'capturing the first page only; the remainder is at risk if a clear follows.',
    )
  }
  // Classify capture_items ids by op. A DELETE is a tombstone / admin-delete, not a
  // held count, so it is skipped. A PUT marks a fresh offline create; a PATCH marks
  // an update.
  const createIds = new Set<string>()
  const patchedIds = new Set<string>()
  // Keep the capture_items ENTRIES (not just ids) so a queued count whose live row was
  // sync-deleted (the bare-row / GONE case) can be rescued from the op's opData+metadata.
  const queuedCaptureOps: Array<(typeof batch.crud)[number]> = []
  for (const e of batch.crud) {
    if (e.table !== 'capture_items' || e.op === 'DELETE') continue
    queuedCaptureOps.push(e)
    if (e.op === 'PUT') createIds.add(e.id)
    else patchedIds.add(e.id)
  }
  // PATCH-only = patched but NEVER PUT in this queue (an offline create+increment has
  // both → it stays a create, the final live row is re-created faithfully).
  const updateOnlyIds = new Set(
    [...patchedIds].filter((id) => !createIds.has(id)),
  )
  if (createIds.size === 0 && updateOnlyIds.size === 0) {
    return empty(batch.haveMore)
  }

  const rows = await db.getAll(QUEUED_CAPTURES_SQL)
  const allLocalIds = new Set(rows.map((r) => String(r['original_item_id'])))
  const createItems: RecoveryDumpItem[] = []
  const updateOnlyItems: RecoveryDumpItem[] = []
  for (const r of rows) {
    const rid = String(r['original_item_id'])
    if (!createIds.has(rid) && !updateOnlyIds.has(rid)) continue
    if (r['rejected_at'] != null) {
      // Synced back down as an admin-rejected TOMBSTONE. Re-creating it would resurrect
      // a count the admin rejected; dropping it (the old `rejected_at IS NULL` filter)
      // silently loses the field's pending work the logout clear would wipe (Codex :378).
      // Route it to the recovery inbox for admin review — the ONE case that does not
      // re-enter the live path.
      updateOnlyItems.push(toQueuedDumpItem(r))
    } else {
      // A LIVE row (rejected_at IS NULL): a fresh offline create (PUT) OR a PATCH-only
      // edit to an already-synced row. BOTH re-enter the live capture path: a create
      // re-INSERTs; an edit collides with its synced-down row and re-applies as a
      // sync_version-bumped PATCH (requeue-stashed-counts.ts :155). Routing edits to the
      // inbox instead made them un-applyable (Phase-2 placement no-ops the existing PK —
      // Codex P1), so live edits ride the re-queue dimension. First-sync gating
      // (auto-reinject-stashed-work.tsx) guarantees the edited row is present for the
      // collision; a create's row is absent (never synced) so it INSERTs cleanly.
      createItems.push(toQueuedDumpItem(r))
    }
  }
  // GONE-ROW rescue (main #830 / Codex P1 — the bare-row case): a queued op whose live
  // capture_items row was already sync-deleted (absent from `rows`, NOT merely rejected —
  // it is absent from allLocalIds entirely) is dropped by the loop above and would then be
  // wiped by the logout clear. Synthesize its count from the queued op's opData +
  // trackMetadata snapshot and route it to the INBOX dimension: a gone-row count cannot
  // re-INSERT as a live capture_items (its FK context is gone), so it never re-queues.
  // DEDUPE BY ID (latest op wins). A create-then-increment offline row emits MULTIPLE
  // queued ops for ONE id; if its live row is gone, emitting one dump item PER OP would
  // POST duplicate original_item_ids in a single chunk, and the inbox's ON CONFLICT DO
  // UPDATE aborts the WHOLE chunk on a same-key repeat (SQLSTATE 21000) — dropping every
  // count in it (2 independent reviewers, live-PG repro; the offline stash is already
  // safe via mergeById). One row per id, latest op, carries the final quantity + the FK
  // snapshot — matching the inbox upsert's latest-wins semantics.
  const goneById = new Map<string, (typeof batch.crud)[number]>()
  for (const e of queuedCaptureOps) {
    if (!allLocalIds.has(e.id) && hasPreservablePayload(e)) goneById.set(e.id, e)
  }
  for (const e of goneById.values()) {
    updateOnlyItems.push(toQueuedDumpItemFromCrud(e))
  }
  return { createItems, updateOnlyItems, truncated: batch.haveMore }
}

/** READ-ONLY: ALL ps_crud-queued capture_items counts as dump items (both ops). Used
 *  by the ONLINE Tier-2 queue rescue, which ships everything to the inbox so the
 *  PUT/PATCH distinction is moot. Shares the single batch read with the split.
 *
 *  LIMITATION (Codex, pre-existing — the ONLINE-logout edge): when a live PATCH-only edit
 *  is still wedged in ps_crud at an ONLINE sign-out, the Tier-2 rescue posts it here to the
 *  inbox, which (like all inbox rows) cannot re-apply an edit to an already-synced row. The
 *  OFFLINE-logout path re-applies edits properly (collectHeldWork routes live rows to the
 *  re-queue → PATCH re-apply); bringing the same patch-capable re-queue to the online Tier-2
 *  path is the online-rescue family's scope (#663 / C4), not this fix. The count is still
 *  preserved in the inbox — only the edit delta is at risk. */
export async function readQueuedItems(
  db: UploadQueueReadableDb,
): Promise<RecoveryDumpItem[]> {
  // Delegates to the split (which does the op-classify + live/rejected/gone routing);
  // the ONLINE Tier-2 rescue ships EVERYTHING to the inbox, so the union of both
  // dimensions is what it needs — including the gone-row counts the split rescues from
  // opData+metadata (Codex P1) and the rejected-tombstone rows (:378).
  const { createItems, updateOnlyItems } = await readQueuedItemsSplit(db)
  return [...createItems, ...updateOnlyItems]
}

/**
 * READ-ONLY: ALL of this device's un-uploaded held work — the quarantine AND the
 * ps_crud queue — for the offline-logout stash (held-work-offline-stash.ts) to
 * persist past the clear, split into the two dimensions the on-login re-inject
 * routes DIFFERENTLY (see HeldWorkBundle): `queueItems` are re-queued into the
 * live capture path so they sync, `quarantineItems` ship to the recovery inbox.
 * Never drains ps_crud. The two dimensions are disjoint by construction (a count
 * is queued OR quarantined), and any incidental cross-dimension overlap during the
 * brief queue→quarantine migration window is a documented-safe over-handle (the
 * re-queue is idempotent on the capture_items id and the inbox upserts).
 */
export async function collectHeldWork(
  db: UploadQueueReadableDb,
): Promise<HeldWorkBundle> {
  // Read ORDER matters (race-safety), exactly like getHeldCaptureLoad: the
  // connector quarantines a failed upload by INSERTing into orphaned_capture_items
  // and THEN draining the ps_crud entry. A concurrent read (Promise.all) could
  // snapshot the quarantine BEFORE the insert while reading the queue AFTER the
  // drain → the migrating count is in NEITHER snapshot and the logout clear wipes
  // the only local copy. Reading the QUEUE FIRST then the quarantine SEQUENTIALLY
  // closes that: a count is in the queue OR has already landed in quarantine (which
  // only grows until the clear), so it is caught by one read — at worst an instant
  // over-count (deduped by the stash's per-id merge), never a miss. (Codex P1)
  const { createItems, updateOnlyItems, truncated } = await readQueuedItemsSplit(db)
  const quarantined = await readQuarantinedItems(db)
  // queueItems = every LIVE queued row (fresh offline creates AND PATCH-only edits to
  // already-synced rows) → re-enter the live path so they sync and the queue drains on
  // the device (a create re-INSERTs; an edit re-applies as a sync_version-bumped PATCH —
  // requeue-stashed-counts.ts). quarantineItems = the connector's orphaned quarantine
  // PLUS admin-rejected tombstones — both ship to the recovery inbox (preserved +
  // admin-surfaced; a tombstone must never resurrect as a live count). `truncated` rides
  // through so the stash audit can surface a queue-overflow loss loudly (Codex :316).
  return {
    queueItems: createItems,
    quarantineItems: [...quarantined, ...updateOnlyItems],
    truncated,
  }
}
